import { useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { onShowAlerts } from "../presentation/component/popup";
import { useSnackbar } from "./useSnackbar";
import { useAuth } from "./useAuth";
import { useLoading } from "./useLoading";
import MainUseCase from "../usecases/MainUseCase";
import MainApi from "../api/MainApi";
import { dummyOrderItems, Invoice, InvoiceResult } from "../domain/entity/Main";

export function useInvoice(token: string | null | undefined) {
  const { showSnackbar } = useSnackbar();
  const { setExpired } = useAuth();
  const { showLoading } = useLoading();
  const [invoice, setInvoice] = useState<InvoiceResult>();
  const [invoiceLoading, setInvoiceLoading] = useState<boolean>(true);

  const repository = new MainUseCase(new MainApi());

  // Fetch invoice details when token is available
  const { isLoading: isInvoiceLoading } = useQuery({
    enabled: !!token, // Only fetch when token exists
    refetchOnWindowFocus: false,
    retry: false,
    queryKey: ["invoice", token],
    queryFn: async () => repository.getInvoice(),
    onSuccess: (results: InvoiceResult) => {
      // console.log("results INVOICE", results);
      setInvoice(results);
    },
    onError: (error: any) => {
      if (
        error.response === undefined ||
        error.response.data === "Read timed out" ||
        error.response.status === 500
      ) {
        showSnackbar("Error - Terjadi Kendala");
      } else if (
        error.response.data !== undefined &&
        error.response.status !== 401
      ) {
        showSnackbar(error.response.data.responseMessage);
      } else if (error.response.status === 401) {
        setExpired(true);
      } else {
        showSnackbar("Error - Terjadi Kendala");
      }
    },
  });

  // Compute allExcludedTaxes and allIncludedTaxes using useMemo
  const { allExcludedTaxes, allIncludedTaxes } = useMemo(() => {
    if (!invoice) {
      return { allExcludedTaxes: [], allIncludedTaxes: [] };
    }

    const allExcludedTaxes = invoice.orderList.flatMap((item) => {
      const taxes = [];
      if (item.singleTax?.taxMethod && item.singleTax?.taxMethod !== "Inclusive") {
        taxes.push(item.singleTax);
      }
      if (item.compoundTax?.singleTax1?.taxMethod && item.compoundTax?.singleTax1?.taxMethod !== "Inclusive") {
        taxes.push(item.compoundTax?.singleTax1);
      }
      if (item.compoundTax?.singleTax2?.taxMethod && item.compoundTax?.singleTax2?.taxMethod !== "Inclusive") {
        taxes.push(item.compoundTax?.singleTax2);
      }
      return taxes;
    });

    const allIncludedTaxes = invoice.orderList.flatMap((item) => {
      const taxes = [];
      if (item.singleTax && item.singleTax.taxMethod === "Inclusive") {
        taxes.push(item.singleTax);
      }
      if (item.compoundTax?.singleTax1?.taxMethod === "Inclusive") {
        taxes.push(item.compoundTax.singleTax1);
      }
      if (item.compoundTax?.singleTax2?.taxMethod === "Inclusive") {
        taxes.push(item.compoundTax.singleTax2);
      }
      return taxes;
    });

    return { allExcludedTaxes, allIncludedTaxes };
  }, [invoice]);

  useEffect(() => {
    if (!isInvoiceLoading && invoice) {
      setInvoiceLoading(false);
    }
    showLoading(isInvoiceLoading);
  }, [isInvoiceLoading]);

  return { invoice, invoiceLoading, allExcludedTaxes, allIncludedTaxes };
}
